import {
  getAuthUserinfo,
  postAuthGoogle,
  type SsoSignInRequest,
  type UserInfo,
} from "Infrastructure/Api/Api";
import {
  CODE_VERIFIER_KEY,
  JWT_TOKEN_KEY,
  USER_DATA_KEY,
} from "Infrastructure/Constants/AuthConstants";

export const backendAuthenticate = async (
  code: string,
  redirectUri: string,
  codeVerifier: string,
): Promise<UserInfo | null> => {
  try {
    const request: SsoSignInRequest = {
      code,
      redirectUri,
      codeVerifier,
      state: null,
    };

    const response = await postAuthGoogle(request);

    if (response.status === 200) {
      const userInfo = response.data.userInfo;

      if (userInfo.token) {
        localStorage.setItem(JWT_TOKEN_KEY, `Bearer ${userInfo.token}`);
      }

      return userInfo;
    }

    if (response.status === 404) {
      console.error("Authentication failed:", response.data);
    } else if (response.status === 401) {
      console.error("Unauthorized:", response.data);
    } else {
      console.error("Backend authentication failed:", response);
    }

    return null;
  } catch (error) {
    console.error("Error authenticating with backend:", error);
    return null;
  }
};

export const getUserInfo = async (): Promise<UserInfo | null> => {
  try {
    const response = await getAuthUserinfo();

    if (response.status === 200) {
      return response.data;
    }
    console.error("Failed to get user info:", response);
    return null;
  } catch (error) {
    console.error("Error fetching user info:", error);
    return null;
  }
};

export const getJWTToken = (): string | null => {
  return localStorage.getItem(JWT_TOKEN_KEY);
};

export const clearTokens = (): void => {
  localStorage.removeItem(USER_DATA_KEY);
  localStorage.removeItem(JWT_TOKEN_KEY);
  localStorage.removeItem(CODE_VERIFIER_KEY);
};

export const generateCodeVerifier = (): string => {
  const array = new Uint32Array(56 / 2);
  crypto.getRandomValues(array);
  return Array.from(array, (dec) => `0${dec.toString(16)}`.substr(-2)).join("");
};

export const generateCodeChallenge = async (
  verifier: string,
): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export const getAuthUrl = async (): Promise<string> => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const redirectUri = window.location.origin;
  const scope = "openid email profile";
  const responseType = "code";
  const state = btoa(
    JSON.stringify({
      timestamp: Date.now(),
      returnUrl: window.location.pathname,
    }),
  );

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  localStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope,
    response_type: responseType,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    access_type: "offline",
    prompt: "consent",
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};
